import React from "react";
import {
  Box,
  Collapse,
  Typography,
  IconButton,
  Paper,
  alpha,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export type HttpVerb = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

/** Verb colours follow the usual REST-docs convention, mapped onto our palette. */
const useVerbColor = (verb: HttpVerb) => {
  const theme = useTheme();
  const { semanticText } = theme.palette;
  switch (verb) {
    case "GET":
      return { tint: theme.palette.info.main, text: semanticText.info };
    case "POST":
      return { tint: theme.palette.success.main, text: semanticText.success };
    case "PUT":
    case "PATCH":
      return { tint: theme.palette.warning.main, text: semanticText.warning };
    default:
      return { tint: theme.palette.error.main, text: semanticText.error };
  }
};

const EndpointCard: React.FC<{
  title: string;
  verb: HttpVerb;
  path: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, verb, path, expanded, onToggle, children }) => {
  const theme = useTheme();
  const verbColor = useVerbColor(verb);

  return (
    <Paper sx={{ borderRadius: "18px", mb: 1.5, overflow: "hidden" }}>
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        sx={{
          width: "100%",
          border: 0,
          background: "transparent",
          cursor: "pointer",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.25, md: 2 },
          flexWrap: "wrap",
          p: { xs: 1.75, md: 2.25 },
          transition: "background-color 160ms ease",
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
          },
        }}
      >
        <Box
          component="span"
          sx={{
            px: 1,
            py: 0.4,
            minWidth: 56,
            textAlign: "center",
            borderRadius: "8px",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: ".05em",
            color: verbColor.text,
            backgroundColor: alpha(verbColor.tint, 0.13),
            border: `1px solid ${alpha(verbColor.tint, 0.3)}`,
            flexShrink: 0,
          }}
        >
          {verb}
        </Box>

        <Typography
          sx={{
            fontSize: { xs: 15, md: 17 },
            fontWeight: 680,
            letterSpacing: "-0.015em",
            flexShrink: 0,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            fontFamily: "monospace",
            fontSize: { xs: 12, md: 13.5 },
            color: "text.secondary",
            overflowX: "auto",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          {path}
        </Typography>

        <IconButton
          component="span"
          aria-hidden
          sx={{
            flexShrink: 0,
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: theme.transitions.create("transform", {
              duration: theme.transitions.duration.shortest,
            }),
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, pb: 2 }}>
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default EndpointCard;
